import {
  Box,
  brandMark,
  Icons,
  iconSize,
  LanguageToggle,
  Link,
  palette,
  Stack,
  Typography,
  fontStacks,
} from '@munifocus/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

const LOCALE_KEY = 'gov.locale';
export const SUPPORT_EMAIL = 'it-support@munifocus.com';

/**
 * The MuniFocus arc mark in a single tone — used in white on the deep-teal
 * brand panel. Mirrors `BrandMark`'s geometry but lets the caller pick a tint.
 */
function ArcMark({ width = 34, tint, accent }: { width?: number; tint: string; accent: string }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 48 32"
      fill="none"
      sx={{ width, height: (width * 32) / 48, flexShrink: 0 }}
      aria-hidden="true"
    >
      <path d="M5 25 A19 19 0 0 1 43 25" stroke={tint} strokeWidth={3} strokeLinecap="round" />
      <path d="M13 25 A11 11 0 0 1 35 25" stroke={accent} strokeWidth={3} strokeLinecap="round" />
      <circle cx={24} cy={25} r={3.6} fill={accent} />
    </Box>
  );
}

/**
 * The brand lockup rendered in white on the deep-teal panel: arc mark +
 * "MuniFocus · Government Portal" wordmark, the dark-surface counterpart to `BrandMark`.
 */
function BrandLockupOnDark() {
  const { t } = useTranslation();
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
      <ArcMark tint={palette.text.onDarkSoft} accent={brandMark.onDark} />
      <Typography
        component="div"
        sx={{
          fontFamily: fontStacks.display,
          fontWeight: 700,
          fontSize: 20,
          letterSpacing: '-0.01em',
          color: palette.text.onDark,
          whiteSpace: 'nowrap',
        }}
      >
        Muni
        <Box component="span" sx={{ color: brandMark.onDark }}>
          Focus
        </Box>
        <Box component="span" sx={{ color: palette.text.onDarkGhost, fontWeight: 400, mx: 0.875 }}>
          ·
        </Box>
        <Box component="span" sx={{ color: brandMark.onDark, fontWeight: 500 }}>
          {t('gov.nav.portalName')}
        </Box>
      </Typography>
    </Box>
  );
}

/**
 * The deep-teal brand panel shown beside the form on desktop (`md+`). A warm
 * concentric-arc motif sits low and faint behind a white lockup, headline, and
 * a short evidentiary tagline — the professional counterpart to the cream form
 * column. Hidden on phones, where the form pane carries the brandmark itself.
 */
function BrandPanel() {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        flex: '0 0 46%',
        position: 'relative',
        overflow: 'hidden',
        px: { md: 6, lg: 8 },
        py: 7,
        color: palette.text.onDark,
        backgroundColor: palette.brand.rail,
        backgroundImage: `linear-gradient(160deg, ${palette.brand.rail} 0%, ${palette.brand.railHover} 100%)`,
      }}
    >
      {/* Oversized concentric-arc motif, low and faint behind the copy. */}
      <Box
        component="svg"
        viewBox="0 0 48 32"
        fill="none"
        aria-hidden="true"
        sx={{
          position: 'absolute',
          bottom: -80,
          right: -120,
          width: 720,
          opacity: 0.06,
          pointerEvents: 'none',
        }}
      >
        <path d="M2 28 A22 22 0 0 1 46 28" stroke={palette.text.onDark} strokeWidth={0.6} />
        <path d="M9 28 A15 15 0 0 1 39 28" stroke={palette.text.onDark} strokeWidth={0.6} />
        <path d="M16 28 A8 8 0 0 1 32 28" stroke={palette.text.onDark} strokeWidth={0.6} />
      </Box>

      <BrandLockupOnDark />

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <Typography
          sx={{
            fontFamily: fontStacks.display,
            fontWeight: 700,
            fontSize: { md: 34, lg: 40 },
            lineHeight: 1.12,
            letterSpacing: '-0.02em',
            color: palette.text.onDark,
            maxWidth: 460,
          }}
        >
          {t('gov.login.panelHeadline')}
        </Typography>
        <Typography
          sx={{
            mt: 2.5,
            fontSize: 16,
            lineHeight: 1.6,
            color: palette.text.onDarkSoft,
            maxWidth: 420,
          }}
        >
          {t('gov.login.panelBody')}
        </Typography>
      </Box>

      <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
        <Icons.ShieldCheck size={iconSize.lg} color={palette.brand.accentLight} />
        <Typography sx={{ fontSize: 13.5, color: palette.text.onDarkSubtle }}>
          {t('gov.login.panelTrust')}
        </Typography>
      </Stack>
    </Box>
  );
}

/**
 * Shared "Quiet paper" auth layout. On desktop (`md+`) it splits into a
 * deep-teal brand panel and a cream form column; on phones it collapses to the
 * single centred cream column. `top` sits at the top of the form column
 * (brandmark or back link, shown on phones / hidden on desktop behind the
 * panel), `children` is vertically centred, and the footer (language · IT
 * support) is pinned to the bottom of the form column.
 *
 * Every screen in this shell is signed out, so the footer carries no developer
 * chrome: no "Connected" dot and no build version (it only ever showed the
 * package.json placeholder). An "Offline" notice appears when the browser is
 * offline, because sign-in cannot work until it is back.
 */
export function AuthShell({
  top,
  children,
  belowContent,
}: {
  top: ReactNode;
  children: ReactNode;
  /** Optional slot pinned just above the footer (e.g. the dev sign-in panel). */
  belowContent?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language as 'en' | 'fr') ?? 'en';
  const [online, setOnline] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const onLang = (next: 'en' | 'fr') => {
    void i18n.changeLanguage(next);
    localStorage.setItem(LOCALE_KEY, next);
  };

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', backgroundColor: palette.background.default }}>
      <BrandPanel />

      {/*
        The sign-in / recovery screens had no landmarks at all — no `<main>`,
        nothing to skip to (WCAG 1.3.1 / 2.4.1).
      */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: '100dvh',
          display: 'flex',
          justifyContent: 'center',
          // "Quiet paper" warmth gradient — near-white at the top, settling to
          // cream and then a parchment tone behind the footer.
          backgroundImage: `linear-gradient(180deg, ${palette.background.paper} 0%, ${palette.background.default} 48%, ${palette.background.parch} 100%)`,
          backgroundAttachment: 'fixed',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 460,
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            px: { xs: 3, sm: 5 },
            pt: { xs: 4, sm: 6 },
            pb: 3,
          }}
        >
          {/* Brandmark on phones / back link on recovery screens. The desktop
              panel carries the primary brand, but a small mark above the form
              keeps the column anchored and gives the back link a home. */}
          {top}

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              py: 4,
            }}
          >
            {children}
          </Box>

          {belowContent}

          <Box sx={{ pt: 3 }}>
            <Stack
              direction="row"
              sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
            >
              {!online && (
                <Stack
                  direction="row"
                  spacing={0.75}
                  role="status"
                  sx={{ alignItems: 'center', minWidth: 0 }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      flexShrink: 0,
                      backgroundColor: palette.semantic.closed,
                    }}
                  />
                  <Typography
                    sx={{ fontSize: 13, color: palette.text.muted, whiteSpace: 'nowrap' }}
                  >
                    {t('gov.login.offline')}
                  </Typography>
                </Stack>
              )}

              <LanguageToggle
                value={lang}
                onChange={onLang}
                groupLabel={t('common.a11y.languageSwitcher')}
                segmentLabels={{
                  en: t('common.a11y.switchToEnglish'),
                  fr: t('common.a11y.switchToFrench'),
                }}
              />

              <Link
                href={`mailto:${SUPPORT_EMAIL}`}
                underline="always"
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: palette.text.primary,
                  whiteSpace: 'nowrap',
                }}
              >
                {t('gov.login.itSupport')}
              </Link>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

/** Top-of-screen "← Back to sign in" link used by the account-recovery screens. */
export function BackToSignIn() {
  const { t } = useTranslation();
  return (
    <Link
      component={RouterLink}
      to="/login"
      underline="none"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        gap: 0.75,
        fontSize: 14.5,
        fontWeight: 600,
        color: palette.brand.accent,
      }}
    >
      <Icons.ArrowLeft size={iconSize.lg} />
      {t('gov.login.backToSignIn')}
    </Link>
  );
}
